/**
 * Admin Messages API Client
 *
 * Provides API calls for admin message management:
 * - List, create, update, publish, archive messages
 * - View analytics
 */

import { api } from './client';

/**
 * List messages with optional filtering.
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status
 * @param {string} params.message_type - Filter by type
 * @param {number} params.limit - Pagination limit
 * @param {number} params.offset - Pagination offset
 * @returns {Promise<{data: Array, pagination: Object}>}
 */
export async function listMessages(params = {}) {
  const query = new URLSearchParams();
  if (params.status) query.append('status', params.status);
  if (params.message_type) query.append('message_type', params.message_type);
  if (params.limit) query.append('limit', params.limit);
  if (params.offset) query.append('offset', params.offset);

  const queryString = query.toString();
  const path = queryString ? `/admin/messages?${queryString}` : '/admin/messages';
  return await api.get(path);
}

/**
 * Get single message by ID.
 * @param {string} id - Message UUID
 * @returns {Promise<{data: Object}>}
 */
export async function getMessage(id) {
  return await api.get(`/admin/messages/${id}`);
}

/**
 * Create new message as draft.
 * @param {Object} data - Message data
 * @returns {Promise<{data: Object}>}
 */
export async function createMessage(data) {
  return await api.post('/admin/messages', data);
}

/**
 * Update draft message.
 * @param {string} id - Message UUID
 * @param {Object} data - Updated fields
 * @returns {Promise<{data: Object}>}
 */
export async function updateMessage(id, data) {
  return await api.patch(`/admin/messages/${id}`, data);
}

/**
 * Publish a draft message.
 * @param {string} id - Message UUID
 * @returns {Promise<{data: Object}>}
 */
export async function publishMessage(id) {
  return await api.post(`/admin/messages/${id}/publish`);
}

/**
 * Archive a message.
 * @param {string} id - Message UUID
 * @returns {Promise<{data: Object}>}
 */
export async function archiveMessage(id) {
  return await api.post(`/admin/messages/${id}/archive`);
}

/**
 * Get message analytics.
 * @param {string} id - Message UUID
 * @returns {Promise<{data: Object}>}
 */
export async function getAnalytics(id) {
  return await api.get(`/admin/messages/${id}/analytics`);
}

/**
 * Pause a published message.
 * @param {string} id - Message UUID
 * @returns {Promise<{data: Object}>}
 */
export async function pauseMessage(id) {
  return await api.post(`/admin/messages/${id}/pause`);
}

/**
 * Resume a paused message.
 * @param {string} id - Message UUID
 * @returns {Promise<{data: Object}>}
 */
export async function resumeMessage(id) {
  return await api.post(`/admin/messages/${id}/resume`);
}
